'use client';

import { useState } from 'react';
import { MarketplaceStyle } from '@/hooks/useMarketplaceStyles';
import { ChevronLeft, ChevronRight, Eye } from 'lucide-react';
import { cn } from '@/lib/utils';
export interface BaseStyleCardProps {
  style: MarketplaceStyle;
  isSelected?: boolean;
  selectionOrder?: number;
  onClick?: () => void;
  onToggle?: () => void;
  disabled?: boolean;
  onSeeMore?: () => void;
}
function getSlides(style: MarketplaceStyle): string[] {
  return [style.coverImage, ...(style.examples ?? [])];
}
const arrowBtnClass = cn('absolute top-1/2 z-10 -translate-y-1/2 flex h-9 w-9 items-center justify-center rounded-full', 'bg-white/90 dark:bg-neutral-900/90 text-neutral-800 dark:text-neutral-100', 'shadow-md ring-1 ring-neutral-200/80 dark:ring-neutral-700', 'opacity-100 sm:opacity-0 sm:transition-opacity sm:duration-200 sm:group-hover:opacity-100', 'hover:bg-white dark:hover:bg-neutral-800', 'focus:outline-none focus-visible:opacity-100 focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-neutral-400');
export function BaseStyleCard({
  style,
  isSelected = false,
  selectionOrder,
  onClick,
  onToggle,
  disabled = false,
  onSeeMore
}: BaseStyleCardProps) {
  const slides = getSlides(style);
  const multi = slides.length > 1;
  const [index, setIndex] = useState(0);
  const move = (delta: number) => {
    if (!multi) return;
    setIndex(i => (i + delta + slides.length) % slides.length);
  };
  const goPrev = (e: React.MouseEvent) => {
    e.stopPropagation();
    move(-1);
  };
  const goNext = (e: React.MouseEvent) => {
    e.stopPropagation();
    move(1);
  };
  const hover = 'sm:hover:shadow-lg sm:hover:ring-neutral-300/80 sm:dark:hover:ring-neutral-700 sm:hover:-translate-y-1 sm:transition-all sm:duration-300 sm:ease-out';
  const isClickable = Boolean(onClick ?? onToggle);
  const cardClassName = cn('group flex flex-col w-full rounded-xl overflow-hidden text-left', 'bg-white dark:bg-neutral-900 shadow-sm ring-1 ring-neutral-200/80 dark:ring-neutral-800', isClickable && (onToggle && disabled && !isSelected ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'), hover);
  const content = <>
      <div className="relative aspect-[3/4] w-full overflow-hidden rounded-t-xl select-none">
        <div className="flex h-full transition-transform duration-300 ease-out" style={{
        transform: `translateX(-${index * 100}%)`
      }}>
          {slides.map((src, i) => <div key={i} className="min-w-full shrink-0 basis-full">
              <img src={src} alt={`${style.name} ${i + 1}`} className="h-full w-full object-cover" draggable={false} />
            </div>)}
        </div>

        {isSelected && selectionOrder != null && <div className="absolute top-2 right-2 sm:top-3 sm:right-3 z-10 flex h-6 w-6 sm:h-8 sm:w-8 items-center justify-center rounded-full bg-primary">
            <span className="text-primary-foreground text-xs font-bold sm:text-sm">
              {selectionOrder}
            </span>
          </div>}

        {multi && !disabled && <>
            <button type="button" onClick={goPrev} className={cn(arrowBtnClass, 'left-2')} aria-label="Previous image">
              <ChevronLeft className="h-5 w-5" />
            </button>
            <button type="button" onClick={goNext} className={cn(arrowBtnClass, 'right-2')} aria-label="Next image">
              <ChevronRight className="h-5 w-5" />
            </button>
          </>}
      </div>

      <div className="flex items-center justify-between gap-3 p-4 rounded-b-xl bg-neutral-50/80 dark:bg-neutral-800/40">
        <p className="min-w-0 flex-1 text-sm font-medium leading-snug text-neutral-800 dark:text-neutral-100 line-clamp-2">
          {style.name}
        </p>
        {onSeeMore && <button type="button" onClick={e => {
        e.stopPropagation();
        onSeeMore();
      }} className={cn('shrink-0 flex h-10 w-10 items-center justify-center rounded-full', 'ring-1 ring-neutral-300 dark:ring-neutral-600 sm:transition-all sm:duration-200', 'hover:bg-neutral-200 hover:text-neutral-900 hover:ring-neutral-400', 'dark:hover:bg-neutral-700 dark:hover:text-white dark:hover:ring-neutral-500', 'focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-neutral-400')} aria-label="See more">
            <Eye className="h-5 w-5" />
          </button>}
      </div>
    </>;
  const handleClick = () => {
    if (onToggle && (!disabled || isSelected)) onToggle();
    if (onClick) onClick();
  };
  if (onToggle) {
    return <button type="button" onClick={handleClick} disabled={disabled && !isSelected} className={cardClassName}>
        {content}
      </button>;
  }
  return <div onClick={onClick} className={cardClassName} data-sentry-component="BaseStyleCard" data-sentry-source-file="BaseStyleCard.tsx">
      {content}
    </div>;
}